import { TenantInvoiceStatusFilter, TenantInvoicesResponse, TenantsParkingData } from 'types';
import HttpClient, { HTTPClientParameters, HTTPError } from './HTTPClient';
import cache from './cache';
import { buildQueryParams, downloadFile } from 'utils/helpers';

// Tenants Parking equals bulk rental from BE
type TenantsParkingClientParameters = HTTPClientParameters;
type GetTenantsParkingParams = {
    spotUuid?: string;
    ordering?: string;
};

export const TENANT_INVOICES_PAGE_SIZE = 25;

export type GetTenantInvoicesParams = {
    tenantUuid: string;
    page: number;
    status: TenantInvoiceStatusFilter;
    month?: number;
    year?: number;
};

export type DownloadTenantInvoicesParams = Omit<GetTenantInvoicesParams, 'page'> & { tenantName: string };

class TenantsParkingClient extends HttpClient {
    constructor(params: TenantsParkingClientParameters) {
        super(params);
        this.headers.set('Content-Type', 'application/json');
    }

    @cache(2 * 60000) // 2min of cache
    getTenantsParking({ spotUuid: spot, ordering }: GetTenantsParkingParams): Promise<TenantsParkingData[]> {
        return this.get(
            `bulkrentals${buildQueryParams({
                spot,
                ordering,
            })}`
        );
    }

    getTenantInvoices({
        tenantUuid,
        page,
        status,
        month,
        year,
    }: GetTenantInvoicesParams): Promise<TenantInvoicesResponse> {
        return this.get(
            `tenants/${tenantUuid}/invoices${buildQueryParams({
                page,
                limit: TENANT_INVOICES_PAGE_SIZE,
                status,
                month,
                year,
            })}`
        );
    }

    // The export ignores pagination and returns every invoice matching the filters.
    async downloadTenantInvoicesCSV({
        tenantUuid,
        status,
        month,
        year,
        tenantName,
    }: DownloadTenantInvoicesParams): Promise<void> {
        const response = await this.getRaw(
            `tenants/${tenantUuid}/invoices/export${buildQueryParams({ status, month, year })}`
        );
        if (!response.ok) {
            throw new HTTPError(response.statusText, response.status, null);
        }
        const period = month && year ? `${year}-${String(month).padStart(2, '0')}` : 'all';
        const statusSuffix = status !== 'all' ? `_${status}` : '';
        downloadFile({
            data: await response.blob(),
            fileName: `invoices_${tenantName || tenantUuid}_${period}${statusSuffix}.csv`,
            fileType: 'csv',
        });
    }
}

export default TenantsParkingClient;
